<template>
  <multiform-controller
    :product-name="productName"
    :step-id="stepId"
    :questions="questions"
    :forms-data="bicycles"
    :initial-data="BICYCLE"
    form-label="bicycle"
    :product-title="productTitle"
    :max-amount="10"
    :is-continue-triggered="isContinueTriggered"
    :items-validity="bicyclesValidity"
    @edit-process="$emit('edit-process', $event)"
    @multiform-questions-details="$emit('multiform-questions-details', $event)"
    @multiform-changed="$emit('multiform-changed', $event)"
    @set-data="handleSetBicycles"
    @reset-continue-trigger="$emit('reset-continue-trigger', $event)"
  >
    <template
      #preview="{ data: { data } }"
    >
      <subform-preview
        :preview-list="formatPreviewItem(data)"
        icon="mdi-bicycle"
      />
    </template>
  </multiform-controller>
</template>

<script>
import QuestionBase from './Base';
import SubformPreview from '@/components/SubformController/SubformPreview';
import MultiformController from '@/components/MultiformController/MultiformController';
import { BICYCLE } from '@/payloadTemplates/bicycle';

export default {
  name: 'question-bicycles',
  extends: QuestionBase,
  components: {
    MultiformController,
    SubformPreview,
  },
  props: {
    productTitle: {
      type: String,
      required: true,
    },
    isContinueTriggered: {
      type: Boolean,
      required: true,
    },
  },
  data () {
    return {
      bicycles: [],
      bicyclesValidity: [],
      questions: [
        {
          questionId: 'bicycle-make',
          questionComponent: 'question-base-search-vehicle-make',
          params: {
            customFields: {
              search: 'bicycleMake',
              other: 'otherBicycleMake',
            },
            title: 'What’s the bicycle make?',
            otherInputTitle: 'Other bicycle make',
            otherHelpText: 'Please enter the make of your bicycle.',
            otherInputPlaceholder: 'Enter your bicycle make',
            otherInputMaxLength: 20,
            searchType: 'searchVehicle',
            topGroup: 'most_common_makes',
            isImmediate: true,
            vehicleSystemType: 'bicycle',
            optionsConfig: {
              placeholder: 'Please select or type your make',
              changedPlaceholder: 'Please enter your bicycle make below',
              other: {
                value: 'Other',
                text: 'My make is not listed',
              },
            },
            validationRules: {
              search: {
                required: true,
              },
              other: {
                required: true,
                regex: '^[a-zA-Z0-9\\s\\-]+$',
              },
            },
            validationErrors: {
              search: {
                required: 'Please select the bicycle make.',
              },
              other: {
                required: 'Please enter the make of your bicycle.',
                regex: 'The make of your bicycle may only contain letters, numbers, spaces and -',
              },
            },
          },
        },
        {
          questionComponent: 'question-base-number',
          questionId: 'bicycle-value',
          params: {
            customFields: {
              field: 'bicycleValue',
            },
            title: 'What value is the bicycle?',
            helpText: 'You should enter the value it would cost to replace the bike as new. If you bought it new, enter the recommended retail price. If it was second hand, you can enter the price you paid.',
            inputPrefix: '£',
            placeholder: 'e.g. 1000',
            validationRules: {
              field: {
                minValue: 100,
                maxValue: 999999,
                required: true,
              },
            },
            validationErrors: {
              field: {
                minValue: 'The minimum value accepted is £100.',
                required: 'Please enter the bicycle value.',
              },
            },
          },
        },
        {
          questionId: 'is-electric',
          questionComponent: 'question-base-radio-boolean',
          params: {
            customFields: {
              field: 'isElectric',
            },
            title: 'Is this an e-bike?',
            helpText: 'An e-bike is a bicycle equipped with an electric motor to assist pedalling, normally powered by a rechargeable battery mounted on the bike.',
            validationRules: {
              field: {
                required: true,
              },
            },
          },
        },
        {
          questionComponent: 'question-base-radio-list',
          questionId: 'bicycle-type',
          params: {
            customFields: {
              field: 'bicycleType',
            },
            title: 'What type of bicycle is it?',
            helpText: 'These are the most common types, but you can select "Other" for more options.',
            optionsConfig: {
              otherPlaceholder: 'Please select type of bicycle',
              mainItems: ['Commuter / City', 'Hybrid', 'Mountain', 'Road / Racing'],
              options: {
                BMX: 'BMX',
                'Commuter / City': 'Commuter / City',
                'Cyclo-Cross': 'Cyclo-Cross',
                Folding: 'Folding',
                Gravel: 'Gravel',
                Hybrid: 'Hybrid',
                Mountain: 'Mountain',
                Recumbent: 'Recumbent',
                'Road / Racing': 'Road / Racing',
                Tandem: 'Tandem',
                'Time Trial / Triathlon': 'Time Trial / Triathlon',
                Touring: 'Touring',
                Track: 'Track',
                Trekking: 'Trekking',
              },
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please select your type of bicycle.',
              },
            },
          },
        },
      ],
    };
  },
  created () {
    // exclude object with nullable values from payloadTemplate when form init for the first time
    const stepData = this.stepData.filter(data => !Object.values(data).every(val => val === null));

    if (stepData.length) {
      this.bicycles = stepData;
    }

    this.bicyclesValidity = this.checkBicyclesValidity();
  },
  methods: {
    handleSetBicycles (e) {
      this.bicycles = e;
      this.$emit('input', this.bicycles);
    },
    checkBicyclesValidity () {
      return this.bicycles.map((bicycle) => !bicycle?.errors);
    },
    formatPreviewItem ({ bicycleMake, bicycleValue, isElectric, bicycleType, otherBicycleMake }) {
      return [
        {
          prepend: 'Make:',
          value: otherBicycleMake || bicycleMake,
        },
        {
          prepend: 'Value:',
          value: bicycleValue,
          model: `£ ${this.helpers.getNumberFormatted(bicycleValue)}`,
        },
        {
          prepend: 'Type:',
          value: bicycleType,
        },
        {
          prepend: 'e-Bike:',
          value: isElectric ? 'Yes' : 'No',
        },
      ];
    },
  },
  setup () {
    return {
      BICYCLE,
    };
  },
};
</script>
